import type L from "leaflet";
import type { SiteSeverity } from "@/services";
import { worstSeverity } from "./markers";

export interface ClusterInput {
  id: string;
  lat: number;
  lon: number;
  severity: SiteSeverity;
  label?: string;
  /** 지역 코드(SiteRegion). 지역 기준 클러스터링의 묶음 키다. */
  region?: string | null;
}

export interface Cluster {
  /** 묶음 식별자 — 지역 기준이면 지역 코드, 픽셀 기준이면 대표 POI id. */
  key: string;
  /** 묶인 항목. 1개면 단독 POI 로 그린다. */
  items: ClusterInput[];
  lat: number;
  lon: number;
  severity: SiteSeverity;
}

/**
 * 지역 묶음을 놓을 좌표.
 *
 * 구성원 평균으로 두면 안 되는 경우가 있다 — 경기·인천 현장들은 서울을 **둘러싸고**
 * 있어서 평균이 서울 한복판에 떨어지고, 서울 마커와 겹쳐 둘 다 못 읽는다.
 * 지역을 대표하는 자리는 그 지역의 지리적 중심이지 현장들의 무게중심이 아니다.
 *
 * 값을 못 받으면(GeoJSON 로드 전) 평균으로 되돌아간다.
 */
export type RegionAnchors = Partial<Record<string, { lat: number; lon: number }>>;

/** 구성원 평균 좌표 + 최악 등급. 묶음 대표값을 한 군데서 만든다. */
function toCluster(
  key: string,
  items: ClusterInput[],
  anchor?: { lat: number; lon: number }
): Cluster {
  return {
    key,
    items,
    // 지역 중심이 있으면 그것을, 없으면 구성원 평균을 쓴다.
    lat: anchor?.lat ?? items.reduce((a, m) => a + m.lat, 0) / items.length,
    lon: anchor?.lon ?? items.reduce((a, m) => a + m.lon, 0) / items.length,
    severity: worstSeverity(items.map((m) => m.severity)),
  };
}

/**
 * 지역(SiteRegion) 기준 클러스터링.
 *
 * 좌표가 아니라 **서버가 준 region 값**으로 묶는다. GeoJSON 폴리곤에 점을 넣어 판정할
 * 수도 있지만, 그러면 지도의 묶음과 좌측 "지역별 현황"의 집계가 경계선 근처에서
 * 어긋난다(행정경계 GeoJSON 은 시도 단위라 경기/인천 같은 합성 지역을 표현하지 못한다).
 * 같은 값을 쓰면 두 화면의 숫자가 항상 일치한다.
 *
 * region 이 없는 현장은 묶지 않고 각자 서 있는다 — 임의로 한 덩어리에 넣으면
 * 엉뚱한 지역 목록에 섞여 나온다.
 */
export function clusterByRegion(items: ClusterInput[], anchors: RegionAnchors = {}): Cluster[] {
  const groups = new Map<string, ClusterInput[]>();
  const loners: Cluster[] = [];

  items.forEach((it) => {
    if (!it.region) {
      loners.push(toCluster(it.id, [it]));
      return;
    }
    const bucket = groups.get(it.region);
    if (bucket) bucket.push(it);
    else groups.set(it.region, [it]);
  });

  return [
    ...[...groups].map(([region, group]) => toCluster(region, group, anchors[region])),
    ...loners,
  ];
}

/**
 * 화면 픽셀 거리 기준 클러스터링.
 *
 * `leaflet.markercluster` 를 쓰지 않는 이유: 그 플러그인은 전역 `window.L` 을 확장하는
 * UMD 인데, Vite 는 leaflet 의 ESM 빌드를 쓰고 플러그인은 CJS 빌드를 require 한다.
 * 서로 다른 인스턴스라 `L.markerClusterGroup` 이 우리 쪽에 생기지 않는다(dual-package hazard).
 * 증상도 고약해서 예외가 promise/layout effect 안에서 삼켜지거나 화면이 통째로 죽는다.
 *
 * 전국 현장은 많아야 수십 개라 단순 그리디 묶음으로 충분하다.
 * 기존 KoreaMap 의 `clusterPOIs` 와 같은 방식이다.
 */
export function clusterByPixel(map: L.Map, items: ClusterInput[], distancePx: number): Cluster[] {
  const points = items.map((it) => ({
    it,
    pt: map.latLngToLayerPoint([it.lat, it.lon]),
    taken: false,
  }));

  const clusters: Cluster[] = [];

  points.forEach((p, i) => {
    if (p.taken) return;
    p.taken = true;
    const members = [p];

    for (let j = i + 1; j < points.length; j += 1) {
      const q = points[j]!;
      if (q.taken) continue;
      if (p.pt.distanceTo(q.pt) <= distancePx) {
        q.taken = true;
        members.push(q);
      }
    }

    clusters.push(
      toCluster(
        p.it.id,
        members.map((m) => m.it)
      )
    );
  });

  return clusters;
}
